import { useCallback, useEffect, useSyncExternalStore } from 'react'

// Module-level cache so the resolved number persists across client-side navigations.
let cachedTrackingNumber: string | null = null

// Single shared MutationObserver across all hook instances.
let observer: MutationObserver | null = null
const listeners = new Set<() => void>()

function stripNonDigits(s: string) {
  return s.replace(/[^\d]/g, '')
}

/**
 * Formats a digits-only phone number for display.
 * 10-digit Australian numbers (1300/1800/mobile): "1300 008 801"
 */
function formatPhoneNumber(digits: string) {
  if (digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
  }
  return digits
}

/**
 * Starts a single MutationObserver (shared across all hook instances) that
 * watches for Delacon rewriting tel: link hrefs in the DOM. When a change
 * is detected, the new number is cached and broadcast to all listeners.
 */
function startObserving(fallbackDigits: string) {
  if (observer) return

  observer = new MutationObserver(() => {
    // Check if any tel: link now points to a different number
    const link = document.querySelector<HTMLAnchorElement>('a[href^="tel:"]')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    const num = href.replace('tel:', '').trim()
    if (!num || num === fallbackDigits) return

    // Delacon has rewritten the link — cache and broadcast
    cachedTrackingNumber = formatPhoneNumber(num)
    listeners.forEach(fn => fn())

    // Stop observing — we have what we need
    observer?.disconnect()
    observer = null
  })

  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  })
}

/**
 * Reads the Delacon tracking phone number, which is injected via GTM.
 *
 * Delacon rewrites tel: link hrefs on the page. Because GTM loads
 * asynchronously (strategy="afterInteractive"), this can take several
 * seconds. This hook uses a MutationObserver to detect when Delacon
 * rewrites the links, caches the result, and serves it instantly on
 * subsequent client-side navigations.
 *
 * Fallback chain:
 *   1. Module-level cache (survives client-side navigations)
 *   2. DOM check for already-rewritten tel: links
 *   3. MutationObserver to catch Delacon when it fires
 *   4. The original number from site settings (passed as `fallback`)
 */
/**
 * Subscribe to tracking number changes from the shared MutationObserver.
 * Compatible with useSyncExternalStore's subscribe signature.
 */
function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

function getSnapshot() {
  return cachedTrackingNumber
}

export default function useTrackingNumber(fallback: string) {
  const fallbackDigits = stripNonDigits(fallback)

  const checkDomAndObserve = useCallback(() => {
    if (cachedTrackingNumber) return

    // Check if Delacon has already rewritten a tel: link
    const link = document.querySelector<HTMLAnchorElement>('a[href^="tel:"]')
    if (link) {
      const href = link.getAttribute('href')
      if (href) {
        const num = href.replace('tel:', '').trim()
        if (num && num !== fallbackDigits) {
          cachedTrackingNumber = formatPhoneNumber(num)
          listeners.forEach(fn => fn())
          return
        }
      }
    }

    // Not rewritten yet — start observing
    startObserving(fallbackDigits)
  }, [fallbackDigits])

  useEffect(() => {
    checkDomAndObserve()
  }, [checkDomAndObserve])

  const cached = useSyncExternalStore(subscribe, getSnapshot, () => null)
  return cached ?? formatPhoneNumber(fallback)
}
